import { Parser } from './parser.ts';

class Picker {
  constructor() {
    // 缓存所有叶子节点及其路径
    this.cache = [];
    // 用于伪类（如 first）的计数器
    this.selectorMatchCount = new Map();
  }

  /**
   * 遍历对象树，生成叶子节点缓存。
   * 每个缓存项包含当前节点的 value 和从根节点到该节点的路径 stack
   * @param {Object|Array} obj
   */
  buildCache(obj) {
    this.cache = [];
    const traverse = (node, path) => {
      if (node !== null && typeof node === 'object') {
        if (Array.isArray(node)) {
          for (let i = 0; i < node.length; i++) {
            path.push({ index: i, value: node[i] });
            traverse(node[i], path);
            path.pop();
          }
        } else {
          for (const key in node) {
            path.push({ key, value: node[key] });
            traverse(node[key], path);
            path.pop();
          }
        }
      } else {
        // 基本类型节点存入缓存，复制当前的路径
        this.cache.push({ value: node, stack: path.slice() });
      }
    };
    traverse(obj, []);
  }

  /**
   * 根据给定的选择器 AST 和已经缓存的节点路径，检测该路径是否匹配
   * 这里实现的逻辑与原 matchCached 类似，倒序匹配 selector 的每一项
   * @param {Array} stack - 缓存项中的路径数组
   * @param {Object} ast - 解析后的选择器 AST
   * @param {Map} pseudoCount - 伪类计数器，用于处理 first 伪类
   * @returns {Boolean}
   */
  matchCache(stack, ast, pseudoCount) {
    if (!ast || !ast.items || ast.items.length === 0) {
      return false;
    }
    let pathIndex = stack.length - 1;
    let allMatched = true;
    const matchedPath = [];

    // 倒序匹配每个选择器项
    for (let i = ast.items.length - 1; i >= 0; i--) {
      const item = ast.items[i];
      let matched = false;
      while (pathIndex >= 0) {
        const node = stack[pathIndex];

        // 如果指定了 key 约束且当前节点不符合，则继续往上查找
        if (item.key && node.key !== item.key) {
          pathIndex--;
          continue;
        }

        // 检查属性条件
        let propsMatched = true;
        for (const prop of item.props) {
          let foundMatch = false;
          for (let j = pathIndex; j >= 0; j--) {
            const pathNode = stack[j];
            const nodeValue = pathNode.value && pathNode.value[prop.key];
            switch (prop.operator) {
              case '=':
                if (nodeValue === prop.value) foundMatch = true;
                break;
              case '*=':
                if (nodeValue && nodeValue.includes(prop.value)) foundMatch = true;
                break;
              case '^=':
                if (nodeValue && nodeValue.startsWith(prop.value)) foundMatch = true;
                break;
              case '$=':
                if (nodeValue && nodeValue.endsWith(prop.value)) foundMatch = true;
                break;
              case '~=':
                if (nodeValue && nodeValue.split(/\s+/).includes(prop.value)) foundMatch = true;
                break;
              case '|=':
                if (
                  nodeValue &&
                  (nodeValue === prop.value || nodeValue.startsWith(`${prop.value}-`))
                )
                  foundMatch = true;
                break;
            }
            if (foundMatch) break;
          }
          if (!foundMatch) {
            propsMatched = false;
            break;
          }
        }
        if (!propsMatched) {
          pathIndex--;
          continue;
        }
        // 当前选择器项匹配成功，记录匹配路径
        matched = true;
        matchedPath.push({ node, selectorIndex: i });
        pathIndex--;
        break;
      }
      if (!matched) {
        allMatched = false;
        break;
      }
    }

    if (allMatched) {
      // 对每个匹配的节点检查伪类
      for (const { node, selectorIndex } of matchedPath) {
        const item = ast.items[selectorIndex];
        for (const pseudo of item.fakeClass) {
          switch (pseudo.name) {
            case 'first': {
              const sKey = this.generateSelectorKey(matchedPath, selectorIndex);
              if (!pseudoCount.has(sKey)) {
                pseudoCount.set(sKey, 0);
              }
              if (pseudoCount.get(sKey) > 0) return false;
              pseudoCount.set(sKey, pseudoCount.get(sKey) + 1);
              break;
            }
            case 'last': {
              // 简化处理：可根据需求完善 last 伪类逻辑
              break;
            }
            case 'empty': {
              if (
                node.value &&
                typeof node.value === 'object' &&
                Object.keys(node.value).length > 0
              )
                return false;
              break;
            }
          }
        }
      }
      return true;
    }
    return false;
  }

  /**
   * 生成选择器 key，用于管理伪类计数。把当前匹配路径的信息拼接成一个字符串
   * @param {Array} matchedPath
   * @param {Number} currentIndex
   */
  generateSelectorKey(matchedPath, currentIndex) {
    return matchedPath
      .filter((p) => p.selectorIndex <= currentIndex)
      .map((p) => `${p.selectorIndex}:${p.node.key !== undefined ? p.node.key : p.node.index}`)
      .join('->');
  }

  /**
   * 利用已经构建好的缓存和选择器 AST 进行匹配，并返回所有匹配到的 leaf 节点的 value
   * @param {Object} ast - 解析后的选择器 AST
   * @param {Boolean} unique - 是否去重
   */
  pickWithCache(ast, unique = false) {
    // 重置伪类计数器
    this.selectorMatchCount = new Map();
    const pseudoCount = new Map();
    let result = [];
    for (const entry of this.cache) {
      if (this.matchCache(entry.stack, ast, pseudoCount)) {
        result.push(entry.value);
      }
    }
    if (unique) {
      // 利用 Map 去重
      result = Array.from(new Map(result.map((item) => [item, item])).values());
    }
    return result;
  }
}

/**
 * 组合器函数，将多个数组做笛卡尔积组合
 * @param  {...any} arrs
 */
export function combo(...arrs) {
  const result = [];
  const first = arrs[0];
  if (arrs.length === 1) {
    return first.map((item) => [item]);
  }
  first.forEach((item) => {
    const rest = combo(...arrs.slice(1));
    rest.forEach((restItem) => {
      result.push([item].concat(restItem));
    });
  });
  return result;
}

/**
 * 根据缓存中记录的路径与选择器 AST 进行匹配，再返回匹配节点的 value
 * @param {Object} obj - 要匹配的对象
 * @param {String} selector - 选择器字符串
 * @param {Boolean} unique - 是否去重
 */
export function pick(obj, selector, unique = false) {
  const parser = new Parser();
  const ast = parser.parse(selector);
  const picker = new Picker();
  picker.buildCache(obj);
  return picker.pickWithCache(ast, unique);
}

/**
 * 对指定的键值配置进行匹配：
 * ① 首先调用 $key 生成所有变量参数，然后只遍历一次数据树生成缓存
 * ② 对每个 key 下的每个选择器（会替换 @ 参数），直接在缓存中匹配获取值
 * @param {Object} obj - 要匹配的对象
 * @param {Object} pickConfig - 配置对象，必须包含 $key 生成器
 */
export function pickBy(obj, pickConfig = {}) {
  if (!pickConfig.$key) {
    throw new Error('必须指定$key生成器');
  }
  if (typeof pickConfig.$key !== 'function') {
    throw new Error('$key生成器必须是一个函数');
  }
  const keys = pickConfig.$key();
  const picker = new Picker();
  // 只对数据树遍历一次生成缓存
  picker.buildCache(obj);
  const parser = new Parser();
  const result = [];
  // 处理config
  const extFields = {};
  const needDeleteKeys = {};
  // debugger;
  // debugger;
  for (const k in pickConfig) {
    if (k.startsWith('$')) {
      continue;
    }
    const val = pickConfig[k];
    if (typeof val === 'function') {
      const newVal = val();
      if (!newVal) {
        // 忽略掉没有的字段
        needDeleteKey[k] = 1;
      }
      // 如果是展开字段
      if (k.startsWith('...')) {
        // 返回的结构必定是一个map
        Object.assign(extFields, newVal);
        needDeleteKeys[k] = 1;
        continue;
      }
      if (typeof newVal === 'string') {
        extFields[k] = newVal;
      }
    }
  }
  // 删除无用的字段
  for (const key in needDeleteKeys) {
    delete pickConfig[key];
  }
  // 追加有用的字段
  Object.assign(pickConfig, extFields);

  for (const key of keys) {
    const item = {};
    for (const k in pickConfig) {
      if (k.startsWith('$')) {
        continue;
      }
      // 参数直接映射（例如 @id 表示取 key 中的 id 字段）
      if (pickConfig[k].startsWith('@')) {
        item[k] = key[pickConfig[k].slice(1)];
        continue;
      }
      // 替换选择器中 @ 参数
      const selector = pickConfig[k].replace(/@(\w+)/g, (match, p1) => key[p1]);
      const ast = parser.parse(selector);
      const arr = picker.pickWithCache(ast);
      if (arr.length > 0) {
        item[k] = arr[0];
      } else {
        item[k] = null;
      }
    }
    result.push(item);
  }
  return result;
}
